import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

const FILTERS = [["", "Todos"], ["aprovado", "Aprovados"], ["pendente", "Pendentes"], ["recusado", "Recusados"], ["erro", "Com erro"]];
const COLOR = {
  aprovado: "bg-emerald-100 text-emerald-800",
  pendente: "bg-amber-100 text-amber-800",
  recusado: "bg-red-100 text-red-700",
  erro: "bg-slate-200 text-slate-800",
};
const LABEL = { aprovado: "Aprovado", pendente: "Pendente", recusado: "Recusado", erro: "Erro" };
const brl = (v) => (v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

const MONTH = new Date().toLocaleDateString("pt-BR", { month: "long" });

function PaymentSummary() {
  const { data: s } = useQuery({ queryKey: ["admin-payments-summary"], queryFn: () => api.get("/admin/pagamentos/resumo").then((r) => r.data), refetchInterval: 30000 });
  const cards = [
    ["month-total", brl(s?.month_total), `Recebido em ${MONTH}`, s ? `${s.month_count} pagamento(s) aprovado(s)` : ""],
    ["pro-active", s?.pro_active ?? "–", "Prestadores no Pro agora", "Com plano ativo hoje"],
  ];
  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-2">
      {cards.map(([k, value, label, sub]) => (
        <div key={k} className="po-card" data-testid={`admin-payments-${k}`}>
          <p className="font-display text-3xl font-extrabold text-orange-600" data-testid={`admin-payments-${k}-value`}>{s ? value : "–"}</p>
          <p className="text-base font-semibold text-slate-700">{label}</p>
          <p className="text-sm text-slate-500">{sub}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminPayments() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin-payments", status, q],
    queryFn: () => api.get("/admin/pagamentos", { params: { ...(status && { status }), ...(q.trim() && { q: q.trim() }) } }).then((r) => r.data),
    refetchInterval: 15000,
  });
  return (
    <div>
      <PaymentSummary />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, label]) => (
            <button key={v} onClick={() => setStatus(v)} data-testid={`admin-payment-filter-${v || "all"}`}
              className={`min-h-[44px] rounded-full border-2 px-4 text-base font-bold ${status === v ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
        <label className="relative block w-full lg:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar prestador (nome ou e-mail)" data-testid="admin-payment-search"
            className="min-h-[48px] w-full rounded-2xl border-2 border-slate-300 pl-10 pr-3 text-base focus:border-orange-500 focus:outline-none" />
        </label>
      </div>
      <div className="mt-4 overflow-x-auto rounded-3xl border border-slate-200">
        <table className="w-full min-w-[860px] text-left text-base" data-testid="admin-payments-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr><th className="p-3">Data/hora</th><th className="p-3">Prestador</th><th className="p-3">Forma</th><th className="p-3">Valor</th><th className="p-3">Status</th><th className="p-3">Motivo / detalhe</th><th className="p-3">Origem</th></tr>
          </thead>
          <tbody>
            {data.map((e) => (
              <tr key={e.id} className="border-t border-slate-100" data-testid={`admin-payment-${e.id}`}>
                <td className="p-3 whitespace-nowrap">{new Date(e.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="p-3">{e.provider_name || "—"}{e.provider_email && <span className="block text-sm text-slate-500">{e.provider_email}</span>}</td>
                <td className="p-3 whitespace-nowrap">{e.method || "—"}</td>
                <td className="p-3 whitespace-nowrap">{brl(e.amount)}</td>
                <td className="p-3"><span className={`rounded-full px-3 py-1 text-sm font-bold ${COLOR[e.status]}`} data-testid={`admin-payment-status-${e.id}`}>{LABEL[e.status] || e.status}</span></td>
                <td className="p-3 text-slate-700">{e.detail || "—"}</td>
                <td className="p-3 text-sm text-slate-500">{e.source}</td>
              </tr>
            ))}
            {!isLoading && data.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-600" data-testid="admin-payments-empty">Nenhum aviso de pagamento.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
